import { FC, ChangeEvent, FocusEvent, useMemo, useState } from 'react';
import { FormProps } from '../helpers';
import FormControl from '../form-control';
import TextInput from '../text-input';
import { UiOption } from '@/lib/helpers';
import DomainChipSelect from '../../domain-chip-select';

interface Props extends FormProps {
  value: string;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  onEnter?: (value: string) => void;
  options: UiOption[];
}

const parseEmail = (email: string): [prefix: string, domain: string] => {
  const atIndex = email.indexOf('@');
  if (atIndex === -1) {
    return [email, ''];
  }
  return [email.slice(0, atIndex), email.slice(atIndex + 1)];
};

const EmailWithDomain: FC<Props> = ({
  name,
  value = '',
  onChange,
  onEnter,
  error = '',
  disabled = false,
  required = false,
  readonly = false,
  options = [],
  size = 'medium',
  autoFocus = false,
}) => {
  const [prefix, domain] = useMemo(() => parseEmail(value), [value]);
  const [activePrefix, setActivePrefix] = useState(prefix);
  const firstValidDomain = options[0]?.value ?? '';
  const displayDomain = domain || firstValidDomain;

  const handlePrefixChange = (newPrefix: string) => {
    setActivePrefix(newPrefix);
  };

  const handleDomainChange = (newDomain: string) => {
    onChange?.(`${activePrefix}@${newDomain}`);
  };

  const onBlur = (_value: string, event: FocusEvent<HTMLInputElement>) => {
    const [sanitizedPrefix] = parseEmail(activePrefix);
    setActivePrefix(sanitizedPrefix);
    onChange?.(`${sanitizedPrefix}@${displayDomain}`, event);
  };

  return (
    <FormControl label="" error={error} name={name} size={size}>
      <TextInput
        name={name}
        label="Email"
        onEnter={onEnter}
        value={activePrefix}
        onChange={handlePrefixChange}
        onBlur={onBlur}
        disabled={disabled}
        required={required}
        size={size}
        autoFocus={autoFocus}
        placeholder=""
        readonly={readonly}
        endAdornmentEl={
          <DomainChipSelect
            key="domain-select"
            value={displayDomain}
            options={options}
            onChange={handleDomainChange}
          />
        }
      />
    </FormControl>
  );
};

export default EmailWithDomain;
